import { startTransition, useCallback, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { RevisionFileAction } from "types/generated";
import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import FlashContext from "components/FlashMessages/FlashContext";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Counter from "ds/components/Counter";
import { EmptystateCodeFileColored } from "components/icons/generated";
import EmptyState from "ds/components/EmptyState";
import Section from "ds/components/Section";
import FileUploadDragDropArea from "ds/components/FileUpload/DragDropArea";
import FileUploadFileItem from "ds/components/FileUpload/FileItem";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import useSearchSpaceliftVcsFiles from "shared/SpaceliftVcsRepo/useSearchSpaceliftVcsFiles";
import useGetSpaceliftVcsRepo from "shared/SpaceliftVcsRepo/useGetSpaceliftVcsRepo";
import { makeStringEqualsPredicate } from "utils/predicates";
import { getRepoUrl, REPOS_PAGE_URL } from "utils/spaceliftRepo";

import {
  buildFolderBreadcrumbs,
  buildPathSearch,
  SPACELIFT_VCS_REPO_SORT_STORAGE_KEY,
} from "../helpers";
import useFilesNavigation from "../Files/Table/useFilesNavigation";
import SpaceliftVcsRepoCommitDialog, { CommitArgs } from "../CommitDialog";
import useRepoCommit from "../useRepoCommit";
import styles from "./styles.module.css";
import {
  buildFullFilePaths,
  partitionByConflicts,
  partitionByDuplicates,
  partitionByFileSize,
  readFileAsBase64,
} from "./helpers";

const SpaceliftVcsRepoUploadFiles = () => {
  const { repoId } = useParams<{ repoId: string }>();
  const navigate = useNavigate();
  const { folderPathSegments, folderPath } = useFilesNavigation();

  const { reportError } = useTypedContext(FlashContext);
  const { repo, loading: repoLoading, error: repoError } = useGetSpaceliftVcsRepo({ repoId });
  const { commit, loading: commitLoading } = useRepoCommit();
  const { refetch: searchExistingFiles } = useSearchSpaceliftVcsFiles({
    repoId,
    skip: true,
  });

  const [files, setFiles] = useState<File[]>([]);
  const fileNamesRef = useRef(new Set<string>());
  const [isCommitDialogOpen, setIsCommitDialogOpen] = useState(false);

  const repoUrl = getRepoUrl(repoId!);

  useBreadcrumbs(
    [
      {
        title: "Repos",
        link: getBreadcrumbsBackUrl(REPOS_PAGE_URL, SPACELIFT_VCS_REPO_SORT_STORAGE_KEY),
      },
      {
        title: repo?.name || "",
        link: repoUrl,
      },
      ...buildFolderBreadcrumbs(repoUrl, folderPathSegments),
      {
        title: "Upload files",
      },
    ],
    [repo?.name, repoUrl, folderPathSegments.length]
  );

  const handleFilesAdded = useCallback(
    async (newFiles: File[]) => {
      const { validFiles, oversizedFileNames } = partitionByFileSize(newFiles);

      if (oversizedFileNames.length > 0) {
        reportError({
          title: "Files exceeding 2MB limit",
          message: `The following files were skipped because they exceed the 2MB limit: ${oversizedFileNames.join(", ")}`,
        });
      }

      const { uniqueFiles, duplicateNames } = partitionByDuplicates(
        validFiles,
        fileNamesRef.current
      );

      if (duplicateNames.length > 0) {
        reportError({
          title: "Duplicate files",
          message: `The following files were skipped because they are duplicates: ${duplicateNames.join(", ")}`,
        });
      }

      if (uniqueFiles.length === 0) {
        return;
      }

      let filesToAdd = uniqueFiles;
      const fullPaths = buildFullFilePaths(uniqueFiles, folderPath);

      try {
        const { data } = await searchExistingFiles({
          input: {
            after: null,
            first: fullPaths.length,
            fullTextSearch: null,
            orderBy: null,
            predicates: [makeStringEqualsPredicate("filePath", fullPaths)],
            requestedPage: null,
          },
        });

        const fullPathsSet = new Set(fullPaths);
        const existingPaths = new Set(
          data?.searchRepoFiles.edges
            .map((edge) => edge.node.filePath)
            .filter((path) => fullPathsSet.has(path))
        );

        if (existingPaths.size > 0) {
          const { conflictingPaths, nonConflictingFiles } = partitionByConflicts(
            uniqueFiles,
            existingPaths,
            folderPath
          );

          if (conflictingPaths.length > 0) {
            reportError({
              title: "Files already exist",
              message: `The following files already exist in this folder: ${conflictingPaths.join(", ")}`,
            });
          }

          filesToAdd = nonConflictingFiles;
        }
      } catch {
        // If the check fails, proceed with adding all files
      }

      for (const file of filesToAdd) {
        fileNamesRef.current.add(file.name);
      }

      if (filesToAdd.length > 0) {
        startTransition(() => {
          setFiles((prev) => [...prev, ...filesToAdd]);
        });
      }
    },
    [reportError, folderPath, searchExistingFiles]
  );

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => {
      const removed = prev[index];
      if (removed) {
        fileNamesRef.current.delete(removed.name);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleBack = () => {
    navigate(`${repoUrl}${buildPathSearch(folderPath)}`);
  };

  const handleCommit = async ({ commitMessage, commitDescription }: CommitArgs) => {
    const fullPaths = buildFullFilePaths(files, folderPath);

    const revisionFiles = await Promise.all(
      files.map(async (f, i) => {
        const content = await readFileAsBase64(f);

        return {
          path: fullPaths[i],
          content,
          encrypt: null,
          fileMode: null,
          action: RevisionFileAction.Create,
        };
      })
    );

    commit(revisionFiles, commitMessage, commitDescription, () => {
      setIsCommitDialogOpen(false);
      navigate(`${repoUrl}${buildPathSearch(folderPath)}`);
    });
  };

  const ErrorContent = useErrorHandle(repoError);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (repoLoading && !repo) {
    return <PageLoading />;
  }

  if (!repo) {
    return <NotFoundPage />;
  }

  const hasFiles = files.length > 0;

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>Upload files</ViewHeaderTitle>
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <Button variant="secondary" onPress={handleBack}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={() => setIsCommitDialogOpen(true)}
              disabled={!hasFiles}
            >
              Commit changes
            </Button>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>
      </ViewHeader>

      <Box direction="column" gap="x-large">
        <Box padding="x-large" className={styles.fileUploadWrapper}>
          <FileUploadDragDropArea
            multiple
            onChange={handleFilesAdded}
            className={styles.dropArea}
          />
        </Box>

        <Section
          title="Uploaded files"
          titleSuffix={hasFiles ? <Counter count={files.length} /> : undefined}
          fullbleed
          padding="x-large"
          contentGap="medium"
        >
          {hasFiles ? (
            <Box direction="column" gap="medium">
              {files.map((file, index) => (
                <FileUploadFileItem
                  key={`${file.name}-${index}`}
                  fileName={file.name}
                  onDelete={() => handleRemoveFile(index)}
                />
              ))}
            </Box>
          ) : (
            <EmptyState icon={EmptystateCodeFileColored} caption="There's no uploaded files yet." />
          )}
        </Section>
      </Box>

      <SpaceliftVcsRepoCommitDialog
        variant="upload"
        isOpen={isCommitDialogOpen}
        onClose={() => setIsCommitDialogOpen(false)}
        onCommit={handleCommit}
        loading={commitLoading}
      />
    </>
  );
};

export default SpaceliftVcsRepoUploadFiles;
